import { useEffect, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CheckCircle2, ShieldCheck, Activity } from "lucide-react";
import { assetUrl } from "@/lib/api";

const FEED = [
  { text: "AEPS withdrawal \u00b7 \u20b95,000 \u00b7 SBI ****4821" },
  { text: "biometric verified \u00b7 txn settled in 1.2s" },
  { text: "DMT \u20b912,400 \u2192 HDFC ****9034 \u00b7 UTR 88129045" },
  { text: "BBPS electricity bill \u00b7 \u20b91,840 paid" },
  { text: "commission credited \u00b7 \u20b914.20" },
  { text: "Micro-ATM withdrawal \u00b7 \u20b92,000 \u00b7 PNB ****7712" },
];

function Terminal() {
  const [lines, setLines] = useState(FEED.slice(0, 3));
  const [i, setI] = useState(3);
  useEffect(() => {
    const t = setInterval(() => {
      setLines((prev) => [...prev.slice(-3), FEED[i % FEED.length]]);
      setI((v) => v + 1);
    }, 2200);
    return () => clearInterval(t);
  }, [i]);
  return (
    <div className="rounded-2xl border border-[#1E2D42] bg-[#0B131F] p-5 shadow-[0_40px_90px_-30px_rgba(11,19,31,0.45)]" data-testid="hero-terminal">
      <div className="mb-4 flex items-center justify-between border-b border-slate-800 pb-3">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
        </div>
        <span className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#34D399]">
          <Activity size={11} className="animate-pulse-glow" /> live rails
        </span>
      </div>
      <div className="space-y-2.5 font-mono text-xs">
        {lines.map((l, idx) => (
          <motion.div
            key={`${l.text}-${idx}-${lines.length}`}
            initial={{ opacity: 0, x: 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-slate-400"
          >
            <CheckCircle2 size={12} className="shrink-0 text-[#34D399]" />
            <span className="truncate">{l.text}</span>
          </motion.div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-slate-800 pt-3 font-mono text-[10px] text-slate-500">
        <ShieldCheck size={11} className="text-[#D9A441]" /> PCI-DSS {"\u00b7"} NPCI certified {"\u00b7"} 256-bit encrypted
      </div>
    </div>
  );
}

function SlideImage({ src, alt }) {
  return (
    <div className="relative" data-testid="hero-slide-image">
      <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border-2 border-[#B8860B]/30" />
      <img src={assetUrl(src)} alt={alt} className="relative aspect-[4/3] w-full rounded-2xl object-cover shadow-[0_40px_90px_-30px_rgba(11,19,31,0.4)]" />
    </div>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: 0.08 + i * 0.12, duration: 0.65, ease: [0.16, 1, 0.3, 1] } }),
  exit: { opacity: 0, y: -16, transition: { duration: 0.28 } },
};

function Title({ title, accent }) {
  const parts = accent && title.includes(accent) ? title.split(accent) : null;
  return (
    <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-[#0F1B2B]" data-testid="hero-title">
      <span className="block overflow-hidden pb-[0.2em] -mb-[0.2em]">
        <motion.span
          className="block"
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          exit={{ y: "-110%", transition: { duration: 0.3 } }}
          transition={{ duration: 0.85, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        >
          {parts ? (
            <>
              {parts[0]}
              <span className="text-gold">{accent}</span>
              {parts[1]}
            </>
          ) : (
            title
          )}
        </motion.span>
      </span>
    </h1>
  );
}

export default function Hero({ hero, brand, slides }) {
  const { scrollY } = useScroll();
  const orbY = useTransform(scrollY, [0, 800], [0, 180]);
  const cardY = useTransform(scrollY, [0, 800], [0, -70]);

  const hasSlides = Array.isArray(slides) && slides.length > 0;
  const count = hasSlides ? slides.length : 0;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % count), 6500);
    return () => clearInterval(t);
  }, [count, idx]);

  const s = hasSlides ? slides[idx] : null;
  const badge = hasSlides ? s.badge : hero.badge;
  const subtitle = hasSlides ? s.subtitle : hero.subtitle;
  const ctaLabel = hasSlides ? s.ctaLabel : hero.primaryCtaLabel;
  const ctaHref = hasSlides ? s.ctaHref : hero.primaryCtaHref;

  return (
    <section id="top" className="relative overflow-hidden bg-gradient-to-b from-[#FAF9F5] via-[#F5F2EB] to-[#FAF9F5] pt-36 pb-24 lg:pt-44 lg:pb-28" data-testid="hero-section">
      <motion.div style={{ y: orbY }} className="pointer-events-none absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#B8860B]/10 blur-[140px]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(15,27,43,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(15,27,43,0.045)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            <motion.div
              key={hasSlides ? `slide-${idx}` : "static"}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <motion.div variants={fadeUp} custom={0}
                className="mb-7 inline-flex items-center gap-2 rounded-full border border-[#B8860B]/30 bg-[#B8860B]/5 px-4 py-1.5"
                data-testid="hero-badge"
              >
                <span className="h-1.5 w-1.5 animate-pulse-glow rounded-full bg-[#B8860B]" />
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9A6A15]">{badge}</span>
              </motion.div>

              {hasSlides ? (
                <Title title={s.title} accent={s.accentWord} />
              ) : (
                <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight text-[#0F1B2B]" data-testid="hero-title">
                  {(hero.titleLines || []).map((line, i) => (
                    <span key={i} className="block overflow-hidden pb-1">
                      <motion.span
                        className="block"
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                      >
                        {hero.accentWord && line.includes(hero.accentWord) ? (
                          <>
                            {line.replace(hero.accentWord, "")}
                            <span className="text-gold">{hero.accentWord}</span>
                          </>
                        ) : (
                          line
                        )}
                      </motion.span>
                    </span>
                  ))}
                </h1>
              )}

              <motion.p variants={fadeUp} custom={2}
                className="mt-6 max-w-xl text-base leading-relaxed text-[#5A6578] sm:text-lg"
                data-testid="hero-subtitle"
              >
                {subtitle}
              </motion.p>

              <motion.div variants={fadeUp} custom={3} className="mt-9 flex flex-wrap items-center gap-4">
                <a href={ctaHref || "#contact"} data-testid="hero-primary-cta"
                  className="group flex items-center gap-2 rounded-full bg-[#1B3A5C] px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-all duration-300 hover:bg-[#0E8A8A] hover:shadow-[0_16px_40px_-12px_rgba(14,138,138,0.5)] active:scale-95">
                  {ctaLabel}
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <a href={hero.secondaryCtaHref || "#services"} data-testid="hero-secondary-cta"
                  className="rounded-full border border-[#E2DDD5] bg-white px-7 py-3.5 text-sm font-semibold text-[#0F1B2B] shadow-sm transition-colors duration-300 hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A] active:scale-95">
                  {hero.secondaryCtaLabel || "Explore Services"}
                </a>
              </motion.div>
            </motion.div>
          </AnimatePresence>

          {count > 1 && (
            <div className="mt-12" data-testid="hero-slider-controls">
              <div className="flex flex-wrap gap-x-7 gap-y-1 border-b border-[#E2DDD5] sm:gap-x-9">
                {slides.map((sl, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    data-testid={`hero-tab-${i}`}
                    className={`relative pb-3.5 pt-1 font-syne text-sm font-bold transition-colors duration-300 sm:text-base ${
                      i === idx ? "text-[#1B3A5C]" : "text-[#A8B2C1] hover:text-[#5A6578]"
                    }`}
                  >
                    {sl.tab || sl.badge || `Slide ${i + 1}`}
                    {i === idx && (
                      <motion.span
                        key={`progress-${idx}`}
                        className="absolute -bottom-px left-0 h-[3px] rounded-full bg-gradient-to-r from-[#B8860B] to-[#0E8A8A]"
                        initial={{ width: "0%" }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 6.5, ease: "linear" }}
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mt-8 font-mono text-xs text-[#8896AB]" data-testid="hero-tagline">{brand.tagline}</p>
        </div>

        <motion.div style={{ y: cardY }} className="lg:col-span-5">
          <AnimatePresence mode="wait">
            <motion.div
              key={hasSlides ? `visual-${idx}` : "visual-static"}
              initial={{ opacity: 0, y: 46, rotate: 1.5 }}
              animate={{ opacity: 1, y: 0, rotate: 0 }}
              exit={{ opacity: 0, y: -28, transition: { duration: 0.3 } }}
              transition={{ duration: 0.85, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              {hasSlides && s.imageUrl ? <SlideImage src={s.imageUrl} alt={s.title} /> : <Terminal />}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
